import React, { useState, useEffect } from 'react';
import { getAllStartups, createStartup, updateStartup, deleteStartup } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';

// Achievement badge component
const AchievementBadge = ({ achievement }) => {
  if (!achievement) return null;
  
  const badges = {
    winner: { label: '🏆 Winner', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
    top3: { label: '🥈 Top 3', className: 'bg-slate-400/20 text-slate-300 border-slate-400/50' },
    top10: { label: '🎯 Top 10', className: 'bg-blue-500/20 text-blue-400 border-blue-500/50' }
  };
  
  const badge = badges[achievement];
  if (!badge) return null;
  
  return <Badge className={`${badge.className} text-xs`}>{badge.label}</Badge>;
};

const AdminStartups = () => {
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    solution: '',
    category: '',
    founder: '',
    stage: '',
    referred_by: '',
    logo_url: '',
    achievement: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    loadStartups();
  }, []);

  const loadStartups = async () => {
    try {
      const data = await getAllStartups();
      setStartups(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading startups:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert empty achievement to null
      const submitData = {
        ...formData,
        achievement: formData.achievement || null
      };
      
      if (editing) {
        await updateStartup(editing, submitData);
      } else {
        await createStartup(submitData);
      }
      loadStartups();
      resetForm();
    } catch (error) {
      console.error('Error saving startup:', error);
      alert('Error saving startup');
    }
  };

  const handleEdit = (startup) => {
    setEditing(startup._id);
    setFormData({
      name: startup.name,
      solution: startup.solution,
      category: startup.category,
      founder: startup.founder,
      stage: startup.stage,
      referred_by: startup.referred_by,
      logo_url: startup.logo_url || '',
      achievement: startup.achievement || '',
      order: startup.order,
      active: startup.active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this startup?')) {
      try {
        await deleteStartup(id);
        loadStartups();
      } catch (error) {
        console.error('Error deleting startup:', error);
        alert('Error deleting startup');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      solution: '',
      category: '',
      founder: '',
      stage: '',
      referred_by: '',
      logo_url: '',
      achievement: '',
      order: 0,
      active: true
    });
    setEditing(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white p-8">Loading...</div>;
  }

  return (
    <AdminLayout currentPage="startups">
      {/* Add Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Startup
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-8 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">
                {editing ? 'Edit Startup' : 'Add New Startup'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Startup Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Category *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g., Cybersecurity, Developer Tools"
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Solution Description *
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={formData.solution}
                    onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                    placeholder="Brief description of what the startup does"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Founder *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.founder}
                      onChange={(e) => setFormData({ ...formData, founder: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Stage *
                    </label>
                    <select
                      required
                      value={formData.stage}
                      onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select stage</option>
                      <option value="Pre-Seed">Pre-Seed</option>
                      <option value="Seed">Seed</option>
                      <option value="Series A">Series A</option>
                      <option value="Series B">Series B</option>
                      <option value="Series C+">Series C+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Referred By *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.referred_by}
                      onChange={(e) => setFormData({ ...formData, referred_by: e.target.value })}
                      placeholder="e.g., Lightspeed, a16z"
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      🏆 Achievement
                    </label>
                    <select
                      value={formData.achievement}
                      onChange={(e) => setFormData({ ...formData, achievement: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No Achievement</option>
                      <option value="winner">🏆 Winner</option>
                      <option value="top3">🥈 Top 3</option>
                      <option value="top10">🎯 Top 10 Finalist</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Logo URL
                    </label>
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-slate-300">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Active (visible on site)
                  </label>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editing ? 'Update' : 'Create'} Startup
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* List */}
        <div className="grid gap-4">
          {startups.map((startup) => (
            <Card key={startup._id} className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white">{startup.name}</h3>
                      <AchievementBadge achievement={startup.achievement} />
                      {!startup.active && (
                        <Badge className="bg-red-500/10 text-red-400 border-red-500/30">Inactive</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mb-2">
                      {startup.category} • {startup.stage} • Founded by {startup.founder}
                    </p>
                    <p className="text-slate-300 mb-2">{startup.solution}</p>
                    <p className="text-xs text-slate-500">
                      Referred by <span className="text-slate-400">{startup.referred_by}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(startup)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(startup._id)}
                      className="border-red-800 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
    </AdminLayout>
  );
};

export default AdminStartups;
